"use client";
import React, { useState } from "react";
import Link from "next/link";
import { Typography, Button, Section } from "@/components/ui";
import { cn } from "@/lib/utils";
import { NewButton } from "./NewButton";

interface FAQItem {
  id: string;
  question: string | React.ReactNode;
  answer: string | React.ReactNode;
}

interface FAQSectionProps {
  id?: string;
  title?: string;
  faqs: FAQItem[];
  initialDisplayCount?: number;
  haveMoreQuestion?: boolean;
  moreQuestionsButtonText?: string;
  moreQuestionsHref?: string;
  moreQuestionsButtonVariant?: "primary" | "outlinePrimary";
  className?: string;
  contentClassName?: string;
}

export function FAQSection({
  id,
  title = "Frequently Asked Questions",
  faqs,
  initialDisplayCount = faqs.length,
  haveMoreQuestion = false,
  moreQuestionsButtonText = "Contact Us",
  moreQuestionsHref = "/company/contact",
  moreQuestionsButtonVariant = "primary",
  className,
  contentClassName,
}: FAQSectionProps) {
  const [openItem, setOpenItem] = useState<string | null>(faqs[0]?.id || null);
  const [showAll, setShowAll] = useState(false);

  const toggleItem = (id: string) => {
    setOpenItem(openItem === id ? null : id);
  };

  const toggleShowAll = () => {
    setShowAll(!showAll);
  };

  const hasMoreFAQs = faqs.length > initialDisplayCount;
  const lastVisibleIndex = (showAll ? faqs.length : Math.min(initialDisplayCount, faqs.length)) - 1;

  return (
    <Section id={id} className={className}>
        <div className={cn(contentClassName)}>
        <div className="text-center mb-12">
          <span className="section-label">FAQ</span>
          <Typography variant="h2" as="h2" className="mb-4">
            {title}
          </Typography>
        </div>

        <div className="flex flex-col gap-12">
          <div className="border border-[#D5D9E2] rounded-xl p-2">
            {faqs.map((faq, index) => (
              // Extra FAQs stay in the HTML (hidden until "View More") so crawlers can read them
              <div
                key={faq.id}
                hidden={!showAll && index >= initialDisplayCount}
                className={cn("bg-white border-b border-[#D5D9E2]", index === lastVisibleIndex && "border-b-0")}
              >
                <button
                  type="button"
                  className="w-full px-5 py-6 text-left flex justify-between gap-4 items-center"
                  onClick={() => toggleItem(faq.id)}
                  aria-expanded={openItem === faq.id}
                >

                  <Typography variant="h4" as="h2" className="font-medium text-base">
                  {faq.id}. {faq.question}
                  </Typography>
                  <span
                    className={`transform transition-transform duration-200 rounded-full bg-gray-100 ${
                      openItem === faq.id ? "rotate-180" : ""
                    }`}
                  >
                    <svg className="w-6 h-6 flex flex-col items-center justify-center p-1 text-text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </span>
                </button>

                {/* Always in the HTML (hidden when closed) so crawlers can read every answer */}
                <div className="px-5 pb-4 pr-8" hidden={openItem !== faq.id}>
                  <div className="text-text-secondary leading-relaxed font-light text-sm [&_a]:text-primary [&_a]:underline [&_ul]:list-disc [&_ul]:pl-5 [&_li]:mt-1">
                    {typeof faq.answer === "string" ? (
                      <div dangerouslySetInnerHTML={{ __html: faq.answer }} />
                    ) : (
                      faq.answer
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {hasMoreFAQs && (
            <div className="text-center">
              <Button
                variant="ghost"
                size="md"
                onClick={toggleShowAll}
                className="p-0 bg-transparent text-primary hover:bg-transparent hover:text-primary focus:ring-0 focus-visible:ring-0 !shadow-none hover:!shadow-none font-medium"
              >
                {showAll ? "View Less" : "View More"}
              </Button>
            </div>
          )}

        {haveMoreQuestion && (
          <div className="w-full rounded-xl md:max-w-[792px] mx-auto flex text-center justify-between items-center px-8 py-7 flex-col md:flex-row gap-3 bg-[#F8F9FF] shadow">
            <div className="flex items-center md:items-start flex-col gap-1">
              <Typography variant="h3" as="h2" className="font-bold text-2xl">
                Still have questions?
              </Typography>
              <Typography variant="body" as="p" className="text-text-primary font-medium">
                Our team is here to help with anything not covered above.
              </Typography>
            </div>
            <Link href={moreQuestionsHref} data-mixpanel-id="homepage-contact-us">
              <NewButton
                variant={moreQuestionsButtonVariant}
                size="md"
                className="w-[175px]"
                arrow={moreQuestionsButtonVariant === "primary" ? "grow" : undefined}
                asChild
              >
                {moreQuestionsButtonText}
              </NewButton>
            </Link>
          </div>
        )}
        </div>
        </div>

    </Section>
  );
}
